import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronUp, ChevronDown, Search } from 'lucide-react';
import { useLanguage } from '../i18n';

/**
 * "Find in conversation" bar (#352).
 *
 * INPUT LAG. This component used to own a `useMemo` that ran a fresh regex over
 * `msg.content` for EVERY message in the thread, keyed on `searchQuery` — i.e. on
 * every keystroke, synchronously, during render, before the new character could be
 * painted. On a long conversation that is exactly the reported "letters do not
 * appear in real time". The whole computation is gone: the count now comes from the
 * parent, which reads it off the rendered marks (`useInChatSearch`) rather than
 * recomputing it from the source text. Typing only re-renders this one small input.
 *
 * `totalMatches` / `currentMatchIndex` are therefore PROPS now, not local state —
 * a single source of truth shared with the thing that does the scrolling, so the
 * counter and the highlight can never disagree.
 *
 * Search box Overlap (#382) — RE-OPENED. This bar used to be an
 * `absolute top-0 left-0 right-0` OVERLAY. `MainOfficeView` renders it as a
 * sibling directly after `<Header>`, and the chat pane those two share
 * (`chat-drop-zone`) is `position: relative` — so `top: 0` pinned the bar to
 * the very TOP of that pane, which is exactly where the Header itself sits
 * (title/status on the left, Search/Share/Bell/close on the right). The two
 * elements occupied the same rectangle: the input covered the conversation
 * title/status, the search bar's own Prev/Next arrows landed on top of the
 * Header's Share and Bell icons, and the search bar's own X sat almost
 * exactly over the Header's chat-close X — one stray click closed the whole
 * Main Chat instead of the search bar. Nudging pixels (a fixed `top` offset
 * matched to the Header's height) would have been fragile — that height
 * changes with the read-only banner, a long title wrapping, and any future
 * Header edit — so the fix is structural instead: this bar is no longer
 * absolutely positioned at all. It renders as an ORDINARY row in the same
 * flex column as the Header and the message list, so it can only ever sit
 * where its DOM position already puts it — directly BELOW the Header,
 * pushing the message list down while open. Two rows can never occupy the
 * same rectangle, so every reported collision (the title, the arrows-vs-
 * icons, the two X's) is gone by construction, not by measurement.
 */
const MessageSearch = ({
    conversationId, isOpen, onClose, onNext, onPrevious,
    onSearchQueryChange, totalMatches = 0, currentMatchIndex = -1, initialQuery = '',
}) => {
    const { t } = useLanguage();
    const [searchQuery, setSearchQuery] = useState('');
    const searchInputRef = useRef(null);
    const debounceRef = useRef(null);

    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isOpen]);

    // Sync initialQuery from global search into the local search bar
    useEffect(() => {
        if (initialQuery && isOpen) {
            setSearchQuery(initialQuery);
        }
    }, [initialQuery, isOpen]);

    useEffect(() => {
        // Clear search when conversation changes
        setSearchQuery('');
    }, [conversationId]);

    // Reset all local state whenever the bar closes (X / Esc). The component stays
    // mounted (it renders null when closed), so without this the internal query
    // lingers — the debounce would then re-push it to the parent (e.g. a pending
    // timer at close time, or a match-count change), making highlights reappear
    // after closing, and re-opening would show the stale term. Resetting here
    // cancels the pending debounce and guarantees a clean search state on close.
    useEffect(() => {
        if (!isOpen) setSearchQuery('');
    }, [isOpen]);

    // Debounce only the SEARCH (re-highlighting the thread), never the input echo:
    // `searchQuery` is applied to the field immediately on change, and only its
    // propagation to the parent waits. That distinction is the lag fix.
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            onSearchQueryChange(searchQuery.trim());
        }, 300);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
        // Keyed on searchQuery only. onSearchQueryChange is not memoized by the
        // parent, so listing it would re-run the debounce on every parent render.
    }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

    const handlePrevious = () => onPrevious();
    const handleNext = () => onNext();

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            onClose();
        } else if (e.key === 'Enter') {
            if (e.shiftKey) {
                handlePrevious();
            } else {
                handleNext();
            }
        }
    };

    if (!isOpen) return null;

    return (
        // Search box Overlap (#382) — an ordinary flow row, NOT an absolute
        // overlay. It sits between <Header> and the message list in the same
        // flex column (see MainOfficeView), so it renders exactly once, below
        // the Header, and can never occupy the Header's own rectangle. `w-full`
        // replaces the old `absolute inset-x-0`; `flex-shrink-0` keeps it from
        // being squeezed by the scroll area below it.
        <div data-testid="chat-search-bar" className="w-full flex-shrink-0 bg-white/[0.03] border-b border-pai-subtle shadow-modal-sm p-4">
            <div className="max-w-3xl mx-auto">
                <div className="flex items-center gap-3 flex-wrap">
                    {/* Search Input */}
                    <div className="flex-1 min-w-[160px] relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-pai-faint" size={18} />
                        <input
                            ref={searchInputRef}
                            data-testid="chat-search-input"
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={t('navFindInConversation', 'Find in conversation...')}
                            className="w-full pl-10 pr-4 py-2 border border-pai rounded-lg bg-white/[0.06] text-white placeholder-white/35 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>

                    {/* Results Counter */}
                    {searchQuery.trim() && (
                        <div data-testid="chat-search-counter" data-total={totalMatches} data-index={currentMatchIndex} className="flex items-center gap-2 text-sm text-pai-meta min-w-[80px] flex-shrink-0">
                            {totalMatches > 0 ? (
                                <span>{currentMatchIndex + 1} / {totalMatches}</span>
                            ) : (
                                <span>{t('navNoResults', 'No results')}</span>
                            )}
                        </div>
                    )}

                    {/* Navigation Buttons */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                            onClick={handlePrevious}
                            data-testid="chat-search-prev"
                            disabled={totalMatches === 0}
                            className="p-2 text-pai-meta hover:text-white  hover:bg-white/[0.07] rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            title={t('navPreviousMatch', 'Previous match (Shift+Enter)')}
                        >
                            <ChevronUp size={20} />
                        </button>
                        <button
                            onClick={handleNext}
                            data-testid="chat-search-next"
                            disabled={totalMatches === 0}
                            className="p-2 text-pai-meta hover:text-white  hover:bg-white/[0.07] rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            title={t('navNextMatch', 'Next match (Enter)')}
                        >
                            <ChevronDown size={20} />
                        </button>
                    </div>

                    {/* Close Button */}
                    <button
                        onClick={onClose}
                        data-testid="chat-search-close"
                        className="p-2 text-pai-meta hover:text-white  hover:bg-white/[0.07] rounded-lg transition-colors flex-shrink-0"
                        title={t('navCloseSearchEsc', 'Close search (Esc)')}
                    >
                        <X size={20} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MessageSearch;
